type TypedRequest = { type: string }

export type OperationRequestItem<Request extends TypedRequest> = {
  status: string
  op: { request: Request }
}

type RequestType<Request extends TypedRequest> = Request['type']
type RequestOf<Request extends TypedRequest, T extends RequestType<Request>> = Extract<Request, { type: T }>

function shallowMatchRequest<Request extends TypedRequest, T extends RequestType<Request>>(
  request: RequestOf<Request, T>,
  filter: Partial<Omit<RequestOf<Request, T>, 'type'>>
) {
  const keys = Object.keys(filter) as Array<keyof typeof filter>
  for (const key of keys) {
    if (request[key] != filter[key]) {
      return false
    }
  }
  return true
}

function hasRequestType<Request extends TypedRequest, T extends RequestType<Request>>(
  request: Request,
  type: T
): request is RequestOf<Request, T> {
  return request.type == type
}

export function isOptimisticOperationItem<Request extends TypedRequest>(
  item: OperationRequestItem<Request>
) {
  return item.status == 'queued' || item.status == 'running' || item.status == 'settled'
}

export function optimisticRequestsByType<Request extends TypedRequest, T extends RequestType<Request>>(
  items: Array<OperationRequestItem<Request>>,
  params: {
    type: T
    filter?: Partial<Omit<RequestOf<Request, T>, 'type'>>
  }
): RequestOf<Request, T>[] {
  const filter = params.filter ?? {}

  return items
    .filter(isOptimisticOperationItem)
    .map((item) => item.op.request)
    .filter((request): request is RequestOf<Request, T> => request.type == params.type)
    .filter((request) => shallowMatchRequest(request, filter))
}

export function optimisticReduce<Base, Request extends TypedRequest, T extends RequestType<Request>>(
  items: Array<OperationRequestItem<Request>>,
  params: {
    base: Base
    type: T
    filter?: Partial<Omit<RequestOf<Request, T>, 'type'>>
    reduce: (base: Base, request: RequestOf<Request, T>) => Base
  }
) {
  const requests = optimisticRequestsByType(items, { type: params.type, filter: params.filter })
  if (requests.length == 0) return params.base
  return requests.reduce(params.reduce, params.base)
}

export function operationStatus<Request extends TypedRequest, T extends RequestType<Request>>(
  items: Array<OperationRequestItem<Request>>,
  filter: { type: T } & Partial<Omit<RequestOf<Request, T>, 'type'>>
): 'idle' | 'running' {
  const type = filter.type
  const requestFilter: Partial<Omit<RequestOf<Request, T>, 'type'>> = filter

  const inFlight = items.some((item) => {
    if (item.status != 'queued' && item.status != 'running') return false
    if (!hasRequestType(item.op.request, type)) return false
    return shallowMatchRequest(item.op.request, requestFilter)
  })

  return inFlight ? 'running' : 'idle'
}
